import { createApi } from '@reduxjs/toolkit/query/react'
import { AxiosError } from 'axios'
import axiosBaseQuery from '@base/axios-base-query'
import FieldWebApi from '@api/FieldWebAPI'
import { serializableError } from '@utils/serializable_error'
import { cleanupFilterParams } from '@features/filtering'
import { cleanupSortParams } from '@features/sorting'

export type CreateField = {
  tableId: string
  variant_type: string
  name: string
}

type DeleteField = {
  tableId: string
  fieldId: string
}

type DeleteFieldResponse = {
  id: string
  type: string
  attributes?: Record<string, unknown>
}

export const fieldsApi = createApi({
  reducerPath: 'fieldsApi',
  baseQuery: axiosBaseQuery({ baseUrl: '/' }), // Use axiosBaseQuery instead of fetchBaseQuery
  endpoints: build => ({
    createField: build.mutation<unknown, CreateField>({
      queryFn: async payload => {
        try {
          const response = await FieldWebApi.createField(payload)
          // Note: Field is in response.data.data
          const field = response.data.data
          return { data: field } // Properly return the response data
        } catch (error) {
          const axiosError = error as AxiosError
          return {
            error: serializableError(axiosError),
          }
        }
      },
    }),
    deleteField: build.mutation<DeleteFieldResponse, DeleteField>({
      queryFn: async payload => {
        try {
          const response = await FieldWebApi.deleteField(payload)
          const field = response.data.data
          return { data: field } // Properly return the response data
        } catch (error) {
          const axiosError = error as AxiosError
          return {
            error: serializableError(axiosError),
          }
        }
      },
      // Add onQueryStarted to handle side effects
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          // Clean up sort params that reference the deleted field
          const deletedFieldId = data.id
          dispatch(cleanupSortParams(deletedFieldId))
          dispatch(cleanupFilterParams(deletedFieldId))
        } catch {
          // If the deletion request fails, we don't need to clean up
        }
      },
    }),
  }),
})

export const { useCreateFieldMutation, useDeleteFieldMutation } = fieldsApi
